import {
  type FC,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Ref,
} from "react";

import {
  Box,
  Column,
  Row as BoxRow,
  SearchInput,
  Skeleton,
  SkeletonBox,
  Text,
} from "@hightouch/htui";
import { matchSorter } from "match-sorter";

import { columnReferenceKey } from "src/components/explore/filter-popover/utils";
import { useDestinationForm } from "src/contexts/destination-form-context";

import type { ColumnOption } from "src/formkit/components/formkit-context";
import type { FormProps, JsonColumnProps } from "src/formkit/components/types";

interface CustomColumnOption {
  label: ColumnOption["label"];
  type?: ColumnOption["type"];
  value: string;
  options?: ColumnOption["options"];
}

const rowSx = {
  alignItems: "center",
  borderBottom: "small",
  fontSize: 0,
  gap: 2,
  minHeight: "32px",
  paddingTop: 1,
  px: 2,
};

interface OnChangeValueProp {
  custom?: boolean;
  from: string | Record<string, unknown>;
  type: "standard";
}

type ArrayPropertiesInputProps = FormProps & {
  columnOptions: CustomColumnOption[];
  jsonColumnProperties: JsonColumnProps;
  onChange: (close: () => void, value: OnChangeValueProp) => void;
  onClose: () => void;
  ref: Ref<HTMLInputElement>;
};

const ArrayPropertiesInput = forwardRef<
  HTMLInputElement,
  Readonly<ArrayPropertiesInputProps>
>(
  (
    {
      onClose,
      columnOptions,
      jsonColumnProperties,
      onChange,
      onReloadEligibleInlineMapperColumns,
      value,
    },
    ref,
  ) => {
    const [search, setSearch] = useState("");
    const selectedColumnRef = useRef<HTMLDivElement>(null);
    const { loadingRows, rows, reloadRows } = useDestinationForm();

    const options = useMemo(() => {
      if (search) {
        const filteredColumns = matchSorter(columnOptions ?? [], search, {
          keys: ["label"],
        });
        return filteredColumns;
      }

      return columnOptions;
    }, [columnOptions, search]);

    // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
    // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
    // TODO with an explanation of why we don't want all deps listed.
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
    const searchInputExists = useMemo(() => {
      if (search === value.from) return true;

      for (const option of options ?? []) {
        if (option.label === search) return true;
      }
      return false;
    }, [search, value.from]);

    const handleReloadRows = async () => {
      await onReloadEligibleInlineMapperColumns(value.from);
    };

    /**
     * These two useEffects work together to discover JSON column shape from a
     * live preview:
     * 1. On mount, trigger a preview if we don't have rows to compute from yet.
     * 2. When rows arrive, extract JSON shape from them and load it into state.
     *
     * Ideally `reloadRows` would return a promise with the data so this could
     * be one step; the current implementation uses this two-step pattern
     * instead.
     */

    // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
    // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
    // TODO with an explanation of why we don't want all deps listed.
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
    useEffect(() => {
      if (!rows?.length) {
        reloadRows();
      }
    }, []);

    // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
    // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
    // TODO with an explanation of why we don't want all deps listed.
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
    useEffect(() => {
      handleReloadRows();
    }, [
      rows,
      jsonColumnProperties.selectedColumnProps,
      jsonColumnProperties.allColumnsProps,
    ]);

    useEffect(() => {
      selectedColumnRef.current?.scrollIntoView();
    }, []);

    const handleEnterPress = (event) => {
      if (
        !loadingRows &&
        event.key === "Enter" &&
        search &&
        !searchInputExists
      ) {
        onChange(onClose, {
          ...value,
          from: search,
        });
      }
    };

    // Canonical key for the current selection. Each option carries the same
    // serialization so selection checks are string-equality lookups against
    // this key.
    const valueFromKey = useMemo(
      () => columnReferenceKey(value.from),
      [value.from],
    );

    // Precomputed canonical key per option. Rebuilt only when `options`
    // itself changes, so the per-row render path reads the key from a Map
    // instead of re-stringifying.
    const optionKeyByOption = useMemo(() => {
      const map = new Map<CustomColumnOption, string>();
      for (const option of options ?? []) {
        map.set(option, columnReferenceKey(option.value));
      }
      return map;
    }, [options]);

    return (
      <Column flex={1} pt={1} px={3}>
        <SearchInput
          autoFocus
          placeholder="Search columns…"
          ref={ref}
          value={search}
          width="100%"
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={handleEnterPress}
        />
        {loadingRows ? (
          <ShimmerLoadingState />
        ) : (
          <>
            <Column mt={2} overflow="auto">
              {(options || []).map((option) => {
                const optionKey = optionKeyByOption.get(option) ?? "";
                const selected = optionKey === valueFromKey;
                return (
                  <Row
                    key={option.value}
                    selected={selected}
                    selectedColumnRef={selectedColumnRef}
                    value={option.value}
                    onClickHandler={() => {
                      onChange(onClose, {
                        ...value,
                        from: option.value,
                      });
                    }}
                  />
                );
              })}
              {typeof value.from === "string" &&
                value.from &&
                !(options ?? []).some((o) => o.value === value.from) && (
                  <Row
                    selected
                    selectedColumnRef={selectedColumnRef}
                    value={`Select "${value.from}" as an expected column value…`}
                  />
                )}
              {search && !searchInputExists && (
                <Row
                  selected={columnReferenceKey(search) === valueFromKey}
                  selectedColumnRef={selectedColumnRef}
                  value={`Select "${search}" as an expected column value…`}
                  onClickHandler={() => {
                    onChange(onClose, {
                      ...value,
                      from: search,
                    });
                  }}
                />
              )}
            </Column>
          </>
        )}
      </Column>
    );
  },
);

ArrayPropertiesInput.displayName = "ArrayPropertiesInput";
export { ArrayPropertiesInput };

const Row: FC<{
  onClickHandler?: () => void;
  selected: boolean;
  selectedColumnRef: React.MutableRefObject<HTMLDivElement | null>;
  value: string;
}> = ({ selected, value, onClickHandler, selectedColumnRef }) => {
  return (
    <Box
      _hover={{
        bg: "base.background",
      }}
      alignItems="center"
      bg={selected ? "primary.background" : undefined}
      borderBottomWidth="hairline"
      borderBottomStyle="solid"
      borderColor="base.border"
      cursor="pointer"
      display="flex"
      flex="0"
      minHeight="32px"
      onClick={onClickHandler}
      pointerEvents={selected ? "none" : undefined}
      px={2}
      ref={selected ? selectedColumnRef : undefined}
    >
      <Box isTruncated maxWidth="fit-content">
        <Text
          color={selected ? "primary.base" : "text.primary"}
          fontWeight="medium"
          mr={2}
          size="sm"
        >
          {value}
        </Text>
      </Box>
    </Box>
  );
};

const containersWidth = ["25%", "20%", "35%", "35%", "25%", "20%", "15%"];

const ShimmerLoadingState = () => (
  <Skeleton isLoading={true}>
    {containersWidth.map((width, index) => (
      <BoxRow key={index} sx={rowSx}>
        <Box margin={0} width={width}>
          <SkeletonBox borderRadius="sm" height="18px" width="100%" />
        </Box>
      </BoxRow>
    ))}
  </Skeleton>
);
